<template>
  <div class="how-to-play-page">
    <!-- Header -->
    <section class="page-header">
      <div class="container">
        <h1>{{ t('howToPlay.title') }}</h1>
        <p class="page-subtitle">{{ t('howToPlay.subtitle') }}</p>
      </div>
    </section>

    <!-- Games Instructions -->
    <section class="instructions-section">
      <div class="container">
        <!-- Game Tabs -->
        <div class="game-tabs">
          <button
            v-for="game in games"
            :key="game.id"
            :class="{ active: selectedGame === game.id }"
            @click="selectedGame = game.id"
            class="tab-btn"
          >
            {{ game.name }}
          </button>
        </div>

        <!-- Poker Instructions -->
        <div v-if="selectedGame === 'poker'" class="game-instructions">
          <h2>{{ t('games.poker.title') }}</h2>
          <div class="instruction-card">
            <h3>{{ t('howToPlay.objectiveLabel') }}</h3>
            <p>{{ t('howToPlay.poker.objective') }}</p>
          </div>
          <div class="instruction-card">
            <h3>{{ t('howToPlay.howToPlayLabel') }}</h3>
            <ol>
              <li>{{ t('howToPlay.poker.step1') }}</li>
              <li>{{ t('howToPlay.poker.step2') }}</li>
              <li>{{ t('howToPlay.poker.step3') }}</li>
              <li>{{ t('howToPlay.poker.step4') }}</li>
            </ol>
          </div>
          <div class="instruction-card">
            <h3>{{ t('howToPlay.modes') }}</h3>
            <div class="modes-list">
              <div class="mode-item">
                <strong>{{ t('howToPlay.classicMode') }}</strong>
                <p>{{ t('howToPlay.poker.classic') }}</p>
              </div>
              <div class="mode-item">
                <strong>{{ t('howToPlay.lightningMode') }}</strong>
                <p>{{ t('howToPlay.poker.lightning') }}</p>
              </div>
              <div class="mode-item">
                <strong>{{ t('howToPlay.coopMode') }}</strong>
                <p>{{ t('howToPlay.poker.coop') }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Sudoku Instructions -->
        <div v-if="selectedGame === 'sudoku'" class="game-instructions">
          <h2>{{ t('games.sudoku.title') }}</h2>
          <div class="instruction-card">
            <h3>{{ t('howToPlay.objectiveLabel') }}</h3>
            <p>{{ t('howToPlay.sudoku.objective') }}</p>
          </div>
          <div class="instruction-card">
            <h3>{{ t('howToPlay.rules') }}</h3>
            <ol>
              <li>{{ t('howToPlay.sudoku.rule1') }}</li>
              <li>{{ t('howToPlay.sudoku.rule2') }}</li>
              <li>{{ t('howToPlay.sudoku.rule3') }}</li>
              <li>{{ t('howToPlay.sudoku.rule4') }}</li>
            </ol>
          </div>
          <div class="instruction-card">
            <h3>{{ t('howToPlay.tips') }}</h3>
            <p>{{ t('howToPlay.sudoku.tips') }}</p>
          </div>
        </div>

        <!-- Riddles Instructions -->
        <div v-if="selectedGame === 'riddles'" class="game-instructions">
          <h2>{{ t('games.riddles.title') }}</h2>
          <div class="instruction-card">
            <h3>{{ t('howToPlay.objectiveLabel') }}</h3>
            <p>{{ t('howToPlay.riddles.objective') }}</p>
          </div>
          <div class="instruction-card">
            <h3>{{ t('howToPlay.howToPlayLabel') }}</h3>
            <ol>
              <li>{{ t('howToPlay.riddles.step1') }}</li>
              <li>{{ t('howToPlay.riddles.step2') }}</li>
              <li>{{ t('howToPlay.riddles.step3') }}</li>
            </ol>
          </div>
          <div class="instruction-card">
            <h3>{{ t('howToPlay.difficulties') }}</h3>
            <div class="modes-list">
              <div class="mode-item">
                <strong>{{ t('howToPlay.easyLevel') }}</strong>
                <p>{{ t('howToPlay.riddles.easy') }}</p>
              </div>
              <div class="mode-item">
                <strong>{{ t('howToPlay.mediumLevel') }}</strong>
                <p>{{ t('howToPlay.riddles.medium') }}</p>
              </div>
              <div class="mode-item">
                <strong>{{ t('howToPlay.hardLevel') }}</strong>
                <p>{{ t('howToPlay.riddles.hard') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <h2>{{ t('howToPlay.ready') }}</h2>
        <button class="btn-play" @click="goToGames">{{ t('buttons.startPlaying') }}</button>
        <button class="btn-back" @click="goHome">{{ t('buttons.backHome') }}</button>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from '@/utils/i18n'

const { t } = useI18n()
const selectedGame = ref('poker')

const games = [
  { id: 'poker', name: 'Word Poker' },
  { id: 'sudoku', name: 'Sudoku' },
  { id: 'riddles', name: 'Brain Teasers' }
]

const goToGames = () => {
  window.location.hash = '#/games/poker'
}

const goHome = () => {
  window.location.hash = '#/'
}
</script>

<style scoped>
.how-to-play-page {
  min-height: 100vh;
  background: var(--color-bg-light);
}

/* Header */
.page-header {
  background: var(--gradient-primary);
  color: white;
  padding: var(--space-5xl) var(--space-base);
  text-align: center;
}

.page-header h1 {
  font-size: clamp(28px, 6vw, 48px);
  margin: 0 0 var(--space-base) 0;
  font-weight: var(--font-weight-bold);
}

.page-subtitle {
  font-size: var(--font-size-body);
  margin: 0;
  opacity: 0.95;
}

/* Content */
.container {
  max-width: 1000px;
  margin: 0 auto;
}

.instructions-section {
  padding: var(--space-4xl) var(--space-base);
}

/* Game Tabs */
.game-tabs {
  display: flex;
  gap: var(--space-md);
  justify-content: center;
  margin-bottom: var(--space-3xl);
  flex-wrap: wrap;
}

.tab-btn {
  padding: var(--space-md) var(--space-lg);
  border: 2px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-light);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out));
  font-family: var(--font-heading);
  font-size: var(--font-size-body);
}

.tab-btn:hover {
  border-color: var(--color-primary-orange);
  color: var(--color-primary-orange);
}

.tab-btn.active {
  background: var(--gradient-primary);
  color: white;
  border-color: var(--color-primary-orange);
}

/* Instructions */
.game-instructions h2 {
  font-size: var(--font-size-h3);
  color: var(--color-text-primary);
  margin: 0 0 var(--space-2xl) 0;
  font-weight: var(--font-weight-bold);
  text-align: center;
}

.instruction-card {
  background: var(--color-bg-card);
  padding: var(--space-2xl);
  border-radius: var(--radius-lg);
  margin-bottom: var(--space-xl);
  border: 1px solid var(--color-border);
}

.instruction-card h3 {
  font-size: var(--font-size-h5);
  color: var(--color-text-primary);
  margin: 0 0 var(--space-lg) 0;
  font-weight: var(--font-weight-bold);
}

.instruction-card p {
  font-size: var(--font-size-body);
  color: var(--color-text-secondary);
  line-height: 1.8;
  margin: 0;
}

.instruction-card ol {
  margin: 0;
  padding-left: var(--space-2xl);
  color: var(--color-text-secondary);
  font-size: var(--font-size-body);
  line-height: 1.8;
}

.instruction-card li {
  margin-bottom: var(--space-sm);
}

/* Modes/Difficulties List */
.modes-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--space-lg);
  margin-top: var(--space-lg);
}

.mode-item {
  padding: var(--space-lg);
  background: var(--color-bg-light);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
}

.mode-item strong {
  display: block;
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  margin-bottom: var(--space-sm);
  font-weight: var(--font-weight-bold);
}

.mode-item p {
  font-size: var(--font-size-small);
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.6;
}

/* CTA Section */
.cta-section {
  background: var(--gradient-primary);
  color: white;
  padding: var(--space-4xl) var(--space-base);
  text-align: center;
}

.cta-section h2 {
  font-size: var(--font-size-h3);
  margin: 0 0 var(--space-2xl) 0;
  font-weight: var(--font-weight-bold);
}

.btn-play {
  display: inline-block;
  padding: var(--space-md) var(--space-2xl);
  background: var(--color-bg-light);
  color: var(--color-primary-orange);
  border: none;
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out));
  font-family: var(--font-heading);
  font-size: var(--font-size-body);
  margin-right: var(--space-lg);
  margin-bottom: var(--space-lg);
}

.btn-play:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.btn-back {
  display: inline-block;
  padding: var(--space-md) var(--space-2xl);
  background: transparent;
  color: white;
  border: 2px solid white;
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out));
  font-family: var(--font-heading);
  font-size: var(--font-size-body);
}

.btn-back:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-4px);
}

/* Responsive */
@media (max-width: 640px) {
  .page-header {
    padding: var(--space-3xl) var(--space-base);
  }

  .instructions-section {
    padding: var(--space-2xl) var(--space-base);
  }

  .game-tabs {
    gap: var(--space-sm);
  }

  .tab-btn {
    padding: var(--space-sm) var(--space-md);
    font-size: var(--font-size-small);
  }

  .instruction-card {
    padding: var(--space-lg);
    margin-bottom: var(--space-lg);
  }

  .modes-list {
    grid-template-columns: 1fr;
    gap: var(--space-md);
  }

  .cta-section {
    padding: var(--space-3xl) var(--space-base);
  }

  .btn-play,
  .btn-back {
    display: block;
    width: 100%;
    margin-right: 0;
    margin-bottom: var(--space-md);
  }

  .btn-play:last-child {
    margin-bottom: 0;
  }
}
</style>
